import {
  SEO_VARIANT_CRYPTO_CREATOR,
  SEO_VARIANT_CRYPTO_TOKEN_CREATOR,
  SEO_VARIANT_TOKEN_CREATOR,
} from "./seoContent";

/** Cross-links between SPL and crypto SEO token creator routes. */
export const CRYPTO_CREATOR_LINK = {
  href: "/crypto-creator",
  label: "Crypto creator — launch your project on Solana",
};

export const CRYPTO_TOKEN_CREATOR_LINK = {
  href: "/crypto-token-creator",
  label: "Crypto token creator — create SPL tokens without code",
};

export const SPL_TOKEN_CREATOR_LINK = {
  href: "/token-creator",
  label: "Solana token creator — create SPL token",
};

const SIBLING_LINKS_BY_VARIANT = {
  [SEO_VARIANT_TOKEN_CREATOR]: [CRYPTO_CREATOR_LINK, CRYPTO_TOKEN_CREATOR_LINK],
  [SEO_VARIANT_CRYPTO_CREATOR]: [SPL_TOKEN_CREATOR_LINK, CRYPTO_TOKEN_CREATOR_LINK],
  [SEO_VARIANT_CRYPTO_TOKEN_CREATOR]: [SPL_TOKEN_CREATOR_LINK, CRYPTO_CREATOR_LINK],
};

const POST_LAUNCH_LINKS = [
  {
    href: "/liquidity-pool-creator",
    label: "Create a liquidity pool for your token",
  },
  { href: "/update-metadata", label: "Update SPL token metadata" },
  {
    href: "/revoke-mint-authority",
    label: "Revoke mint authority on Solana",
  },
  {
    href: "/revoke-freeze",
    label: "Revoke freeze authority on Solana",
  },
  {
    href: "/volume-bot",
    label: "Solana volume bot — boost token volume",
  },
];

export const CRYPTO_CREATOR_RELATED_LINKS = [
  {
    href: "/token-creator",
    label: "Compare with the standard Solana token creator",
  },
  {
    href: "/crypto-token-creator",
    label: "Create SPL tokens with the crypto token creator route",
  },
  {
    href: "/liquidity-pool-creator",
    label: "Open the liquidity pool creator after your crypto launch",
  },
  {
    href: "/update-metadata",
    label: "Refresh SPL metadata after deployment",
  },
  {
    href: "/token-hub",
    label: "Verify your mint in Token Hub before campaigns",
  },
  {
    href: "/volume-bot",
    label: "Run a Solana volume bot for launch visibility",
  },
];

/** Server-rendered related links for /crypto-token-creator (unique anchor text vs site footer). */
export const CRYPTO_TOKEN_CREATOR_RELATED_LINKS = [
  {
    href: "/token-creator",
    label: "Compare with the standard Solana token creator",
  },
  {
    href: "/crypto-creator",
    label: "Launch a broader crypto project on Solana",
  },
  {
    href: "/liquidity-pool-creator",
    label: "Open the liquidity pool creator after minting",
  },
  {
    href: "/update-metadata",
    label: "Refresh SPL metadata after your token is live",
  },
  {
    href: "/revoke-mint-authority",
    label: "Revoke mint authority for a fixed token supply",
  },
  {
    href: "/revoke-freeze",
    label: "Revoke freeze authority to improve holder trust",
  },
  {
    href: "/volume-bot",
    label: "Run a Solana volume bot for launch visibility",
  },
];

/** Related tools block on /token-creator, /crypto-creator, /crypto-token-creator. */
export function getSplTokenCreatorRelatedLinks(seoVariant = SEO_VARIANT_TOKEN_CREATOR) {
  if (seoVariant === SEO_VARIANT_CRYPTO_TOKEN_CREATOR) {
    return CRYPTO_TOKEN_CREATOR_RELATED_LINKS;
  }
  const siblings = SIBLING_LINKS_BY_VARIANT[seoVariant] ?? SIBLING_LINKS_BY_VARIANT[SEO_VARIANT_TOKEN_CREATOR];
  return [...siblings, ...POST_LAUNCH_LINKS];
}

/** Add crypto creator URLs next to existing token-creator links on other tools. */
export const TOKEN_CREATOR_FAMILY_LINKS = [
  SPL_TOKEN_CREATOR_LINK,
  CRYPTO_CREATOR_LINK,
  CRYPTO_TOKEN_CREATOR_LINK,
];

/**
 * @param {{ href: string, label: string }[]} links
 * @returns {{ href: string, label: string }[]}
 */
export function withTokenCreatorFamilyLinks(links) {
  const hasTokenCreator = links.some((l) => l.href === "/token-creator");
  const withoutFamily = links.filter(
    (l) =>
      l.href !== "/token-creator" &&
      l.href !== "/crypto-creator" &&
      l.href !== "/crypto-token-creator",
  );
  if (hasTokenCreator) {
    const idx = links.findIndex((l) => l.href === "/token-creator");
    return [
      ...links.slice(0, idx + 1),
      CRYPTO_CREATOR_LINK,
      CRYPTO_TOKEN_CREATOR_LINK,
      ...links.slice(idx + 1).filter(
        (l) => l.href !== "/crypto-creator" && l.href !== "/crypto-token-creator",
      ),
    ];
  }
  return [...TOKEN_CREATOR_FAMILY_LINKS, ...withoutFamily];
}
